/**
 * TanStack Query hooks for conversion-scrub (FS-823).
 * List query + bulkDecide mutation. Sonner toast on success / error.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	bulkDecide,
	conversionScrubQueryKeys,
	listPendingConversions,
	REASON_LABELS,
	type BulkDecideBody,
	type BulkDecideResponse,
	type ListConversionScrubResponse,
	type ListPendingArgs,
} from "./api";

export function useConversionScrubList(
	args: ListPendingArgs = {},
): UseQueryResult<ListConversionScrubResponse, Error> {
	return useQuery({
		queryKey: [
			...conversionScrubQueryKeys.list(),
			args.campaign ?? null,
			args.affiliate ?? null,
			args.fraud_min ?? null,
			args.from ?? null,
			args.to ?? null,
			args.limit ?? null,
		],
		queryFn: () => listPendingConversions(args),
	});
}

export function useBulkDecide(): UseMutationResult<
	BulkDecideResponse,
	Error,
	BulkDecideBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: bulkDecide,
		onSuccess: (res, vars) => {
			const label = res.decision === "approved" ? "approved" : "rejected";
			const reasonLabel = REASON_LABELS[vars.reason] ?? vars.reason;
			toast.success(
				`${res.updated} conversion${res.updated === 1 ? "" : "s"} ${label} (${reasonLabel}).`,
			);
			qc.invalidateQueries({ queryKey: conversionScrubQueryKeys.all });
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to submit decision.");
		},
	});
}

// ---------------------------------------------------------------------------
// Scaffold-compat aliases (not used in the page, kept for contract parity).
// ---------------------------------------------------------------------------

export function useConversionScrubCreate(): UseMutationResult<
	BulkDecideResponse,
	Error,
	BulkDecideBody
> {
	return useBulkDecide();
}

export function useConversionScrubDelete(): UseMutationResult<
	{ removed: boolean },
	Error,
	string
> {
	return useMutation({
		mutationFn: async () => {
			throw new Error("delete: not supported — use bulkDecide");
		},
	});
}
